import { createFileRoute, Link } from '@tanstack/react-router';
import {
    AlertTriangle,
    ArrowUpRight,
    Clock3,
    Search,
    Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { cn } from '@apps/web/lib/utils';
import { SourceHealthChips } from '@apps/web/routes/_app/admin/agent/source_health';
import { useAdminAgentMissingTools } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AdminAgentMissingToolListResponse } from '@libraries/typescript/api_client/schema_types';

export type MissingToolCapability =
    AdminAgentMissingToolListResponse['capabilities'][number];
type MissingToolReport = MissingToolCapability['reports'][number];
export type MissingToolModeFilter = 'all' | 'live' | 'sandbox';

const EMPTY_CAPABILITIES: ReadonlyArray<MissingToolCapability> = [];

export const Route = createFileRoute('/_app/admin/agent/missing-tools')({
    head: () => ({
        meta: [
            {
                title: 'Missing Tools | Phoebe Admin',
            },
        ],
    }),
    component: AdminAgentMissingToolsPage,
});

export function AdminAgentMissingToolsPage() {
    const [query, setQuery] = useState('');
    const [modeFilter, setModeFilter] = useState<MissingToolModeFilter>('all');
    const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
    const queryParams = useMemo(
        () => ({
            limit: 100,
            report_limit: 10,
            ...(modeFilter === 'all' ? {} : { mode: modeFilter }),
        }),
        [modeFilter],
    );
    const { data, isLoading, error } = useAdminAgentMissingTools(queryParams);
    const capabilities = data?.capabilities ?? EMPTY_CAPABILITIES;
    const filteredCapabilities = useMemo(
        () => filterMissingToolCapabilities(capabilities, query),
        [capabilities, query],
    );
    const selectedCapability = selectMissingToolCapability(
        filteredCapabilities,
        selectedSlug,
    );
    const totalReportCount = data?.total_report_count ?? 0;

    return (
        <div className="bg-background flex h-[calc(100svh-3.5rem)] max-h-[calc(100svh-3.5rem)] min-h-0 w-full flex-col overflow-hidden p-4 md:h-svh md:max-h-svh">
            <div className="shrink-0 border-b pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Wrench className="text-foreground h-5 w-5" />
                            <h1 className="text-foreground text-xl font-semibold">
                                Missing Tools
                            </h1>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <StatBadge
                                label="Capabilities"
                                value={capabilities.length}
                            />
                            <StatBadge
                                label="Reports"
                                value={totalReportCount}
                            />
                            <StatBadge
                                label="Visible"
                                value={filteredCapabilities.length}
                            />
                        </div>
                    </div>
                    <SourceHealthChips />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <div className="relative min-w-[260px] flex-[1_1_340px] md:max-w-md">
                        <Search className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search capability, goal, tool, or org"
                            className="h-8 pl-9 text-sm"
                        />
                    </div>
                    <Select
                        value={modeFilter}
                        onValueChange={(value) =>
                            setModeFilter(value as MissingToolModeFilter)
                        }
                    >
                        <SelectTrigger className="h-8 w-[140px] text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All modes</SelectItem>
                            <SelectItem value="live">Live</SelectItem>
                            <SelectItem value="sandbox">Sandbox</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-3 pt-3">
                <div className="text-muted-foreground flex shrink-0 items-center justify-between text-sm">
                    <span>
                        Showing {filteredCapabilities.length} of{' '}
                        {capabilities.length} capability gaps
                    </span>
                </div>
                {isLoading ? (
                    <EmptyState>Loading missing tool reports</EmptyState>
                ) : error ? (
                    <EmptyState tone="error">
                        Missing tool reports failed to load
                    </EmptyState>
                ) : filteredCapabilities.length === 0 ? (
                    <EmptyState>
                        No missing tool reports match this view
                    </EmptyState>
                ) : (
                    <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(20rem,45%)] overflow-hidden rounded-md border bg-white lg:grid-cols-[minmax(0,1fr)_460px] lg:grid-rows-1">
                        <MissingToolTable
                            capabilities={filteredCapabilities}
                            selectedSlug={
                                selectedCapability?.requested_capability_slug ??
                                null
                            }
                            onSelect={setSelectedSlug}
                        />
                        <MissingToolDetails capability={selectedCapability} />
                    </div>
                )}
            </div>
        </div>
    );
}

function StatBadge({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number;
}) {
    return (
        <span className="text-muted-foreground bg-muted/40 inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
            <span className="font-medium">{label}</span>
            <span className="tabular-nums">{value}</span>
        </span>
    );
}

function EmptyState({
    children,
    tone = 'neutral',
}: {
    readonly children: string;
    readonly tone?: 'neutral' | 'error';
}) {
    return (
        <div
            className={cn(
                'flex min-h-0 flex-1 items-center justify-center rounded-md border bg-white text-sm',
                tone === 'error' ? 'text-red-700' : 'text-muted-foreground',
            )}
        >
            {children}
        </div>
    );
}

function MissingToolTable({
    capabilities,
    selectedSlug,
    onSelect,
}: {
    readonly capabilities: ReadonlyArray<MissingToolCapability>;
    readonly selectedSlug: string | null;
    readonly onSelect: (slug: string) => void;
}) {
    return (
        <div className="min-h-0 overflow-y-auto border-b lg:border-r lg:border-b-0">
            <Table
                className="min-w-[840px]"
                containerClassName="overflow-x-auto"
            >
                <TableHeader className="sticky top-0 z-10 bg-white">
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[32%]">Capability</TableHead>
                        <TableHead className="w-[12%] text-right">
                            Reports
                        </TableHead>
                        <TableHead className="w-[20%]">Latest</TableHead>
                        <TableHead className="w-[18%]">Mode</TableHead>
                        <TableHead>Latest org</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {capabilities.map((capability) => {
                        const selected =
                            capability.requested_capability_slug ===
                            selectedSlug;
                        const latestReport = capability.reports[0];
                        return (
                            <TableRow
                                key={capability.requested_capability_slug}
                                onClick={() =>
                                    onSelect(
                                        capability.requested_capability_slug,
                                    )
                                }
                                className={cn(
                                    'h-12 cursor-pointer',
                                    selected
                                        ? 'bg-sky-50 hover:bg-sky-50'
                                        : 'hover:bg-muted/50',
                                )}
                            >
                                <TableCell className="min-w-0">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                                        <span className="truncate font-medium">
                                            {
                                                capability.requested_capability_slug
                                            }
                                        </span>
                                    </div>
                                    <div className="text-muted-foreground mt-0.5 truncate text-xs">
                                        {capability.display_name}
                                    </div>
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm">
                                    {capability.report_count}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-xs">
                                    {formatTimestamp(
                                        capability.latest_report_at,
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant="secondary"
                                        className="capitalize"
                                    >
                                        {capability.latest_mode}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground max-w-[220px] truncate text-sm">
                                    {latestReport
                                        ? organizationLabel(latestReport)
                                        : capability.latest_organization_id}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

function MissingToolDetails({
    capability,
}: {
    readonly capability: MissingToolCapability | null;
}) {
    if (!capability) {
        return (
            <div className="text-muted-foreground flex min-h-0 items-center justify-center p-4 text-sm">
                Select a missing capability
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 border-b p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-muted-foreground text-xs font-medium">
                            Capability gap
                        </div>
                        <h2 className="mt-1 truncate text-lg font-semibold">
                            {capability.requested_capability_slug}
                        </h2>
                    </div>
                    <Badge variant="outline">
                        {capability.report_count} reports
                    </Badge>
                </div>
                <div className="text-muted-foreground mt-3 flex items-center gap-1.5 text-xs">
                    <Clock3 className="h-3.5 w-3.5" />
                    Latest {formatTimestamp(capability.latest_report_at)}
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                    {capability.reports.map((report) => (
                        <MissingToolReportRow
                            key={report.artifact_id}
                            report={report}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function MissingToolReportRow({
    report,
}: {
    readonly report: MissingToolReport;
}) {
    return (
        <div className="rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">
                        {organizationLabel(report)}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-xs">
                        {formatTimestamp(report.created_at)}
                    </div>
                </div>
                <Badge variant="secondary" className="capitalize">
                    {report.mode}
                </Badge>
            </div>
            <div className="mt-3 space-y-3 text-sm">
                <ReportField label="Goal">{report.user_goal}</ReportField>
                <ReportField label="Closest tools">
                    {report.closest_existing_tools.length > 0
                        ? report.closest_existing_tools.join('; ')
                        : 'None recorded'}
                </ReportField>
                {report.suggested_spec ? (
                    <ReportField label="Suggested spec">
                        {report.suggested_spec}
                    </ReportField>
                ) : null}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <Link
                    to="/admin/agent/$runId"
                    params={{ runId: report.run_id }}
                    className="inline-flex items-center gap-1 rounded-md border px-2 py-1 font-medium text-sky-700 hover:bg-sky-50"
                >
                    Run
                    <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
                <span className="text-muted-foreground truncate">
                    Artifact {report.artifact_id}
                </span>
            </div>
        </div>
    );
}

function ReportField({
    label,
    children,
}: {
    readonly label: string;
    readonly children: string;
}) {
    return (
        <div>
            <div className="text-muted-foreground text-xs font-medium">
                {label}
            </div>
            <div className="mt-1 leading-5 break-words whitespace-pre-wrap">
                {children}
            </div>
        </div>
    );
}

export function filterMissingToolCapabilities(
    capabilities: ReadonlyArray<MissingToolCapability>,
    query: string,
): Array<MissingToolCapability> {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return [...capabilities];
    }
    return capabilities.filter((capability) =>
        missingToolCapabilitySearchText(capability).includes(normalizedQuery),
    );
}

export function selectMissingToolCapability(
    capabilities: ReadonlyArray<MissingToolCapability>,
    selectedSlug: string | null,
): MissingToolCapability | null {
    return (
        capabilities.find(
            (capability) =>
                capability.requested_capability_slug === selectedSlug,
        ) ??
        capabilities[0] ??
        null
    );
}

function missingToolCapabilitySearchText(
    capability: MissingToolCapability,
): string {
    return [
        capability.requested_capability_slug,
        capability.display_name,
        ...capability.reports.flatMap((report) => [
            report.requested_capability,
            report.user_goal,
            report.closest_existing_tools.join(' '),
            report.suggested_spec ?? '',
            organizationLabel(report),
        ]),
    ]
        .join(' ')
        .toLowerCase();
}

function organizationLabel(report: MissingToolReport): string {
    return (
        report.organization_internal_name?.trim() ||
        report.organization_name?.trim() ||
        report.organization_id
    );
}

function formatTimestamp(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}
